import {json_inflate} from "@/prototype/utils/JSON.js";
import Point from "@/prototype/projection/Point.js";
import Filter from "./Filter.js";
import global from "@/prototype/global.js";

// import {get_translation} from "svg-utils";

import {points_bearing, minE, minN, minX, maxX, minY, maxY, dN, dE, aT} from "@/prototype/projection/tools.js";
import {DEGREES} from "@/prototype/projection/constants.js";

const log = global.log.getLogger("f_mtac.scene.projection");
log.setLevel(log.levels.WARN);

const d3 = global.d3;

const build_projector = function (projection) {
    var alphaE = 1,
        betaE = 0,
        alphaN = 1,
        betaN = 0;

    const projector = ([E, N]) => {
        //log.debug("Projection.projector in ", {E,N, alphaE, betaE, alphaN, betaN});
        //log.debug(`Projection.projector projection [lon,lat] = [${alphaE * E + betaE}, ${alphaN * N + betaN}]`);
        var [X, Y] = projection([alphaE * E + betaE, alphaN * N + betaN]);
        //log.debug(`Projection.projector return [X,Y] = [${X}, ${Y}]`);
        return [X, Y];
    };

    Object.defineProperty(projector, "alphaE", {
        set: (newalphaE) => {
            alphaE = newalphaE;
        },
        get: () => alphaE
    });
    Object.defineProperty(projector, "alphaN", {
        set: (newalphaN) => {
            alphaN = newalphaN;
        },
        get: () => alphaN
    });
    Object.defineProperty(projector, "betaE", {
        set: (newbetaE) => {
            betaE = newbetaE;
        },
        get: () => betaE
    });
    Object.defineProperty(projector, "betaN", {
        set: (newbetaN) => {
            betaN = newbetaN;
        },
        get: () => betaN
    });
    Object.defineProperty(projector, "projection", {
        set: (newprojection) => {
            projection = newprojection;
        },
        get: () => projection
    });

    projector.skewE = (A1, B1, A2, B2) => {
        alphaE = dE(A2, B2) / dE(A1, B1);
        betaE = -(Math.pow(dE(A2, B2), 2) - dE(A1, B1) * dE(A2, B2)) / (2 * dE(A1, B1)) - minE(A2, B2) * alphaE + minE(A2, B2);

    };

    projector.skewN = (A1, B1, A2, B2) => {
        alphaN = dN(A2, B2) / dN(A1, B1);
        betaN = -(Math.pow(dN(A2, B2), 2) - dN(A1, B1) * dN(A2, B2)) / (2 * dN(A1, B1)) - minN(A2, B2) * alphaN + minN(A2, B2);
    };

    projector.invert = (XY) => {
        var [E, N] = projection.invert(XY);
        return [(E - betaE) / alphaE, (N - betaN) / alphaN];
    };

    return projector;
};

const Projection = function (context) {
    var projector = null;
    var filter = new Filter();

    var A = null,
        B = null;
    
    // rotation that has been applied to the geonodes (and to the image)
    // for the north to be bottom -> up
    var rotation = 0;

    Object.defineProperty(this, "filter", {
        set: () => {},
        get: () => filter
    });

    this.add_point = (T, PD) => {
        log.debug(`Projection.add_point ${T}`);
        switch (T) {
            case "A":
                this.A = PD;
                break;
            case "B":
                this.B = PD;
                break;
        }
    };

    this.remove_point = (T) => {
        log.debug(`Projection.remove_point ${T}`);
        switch (T) {
            case "A":
                this.A = null;
                break;
            case "B":
                this.B = null;
                break;
        }
    };

    Object.defineProperty(this, "A", {
        set: (PD) => {
            if (PD === null) {
                A = null;
                compute_projection();
                return;
            }
            if (A === null) {
                A = new Point();
            }
            A.set(PD);
            compute_projection();
        },
        get: () => A
    });

    Object.defineProperty(this, "B", {
        set: (PD) => {
            if (PD === null) {
                B = null;
                compute_projection();
                return;
            }
            if (B === null) {
                B = new Point();
            }
            B.set(PD);
            compute_projection();
        },
        get: () => B
    });

    // defined for storage in dataProjection
    Object.defineProperty(this, "rotation", {
        set: (value) => {
            log.debug(`Projection.rotation = ${value}`);
            rotation = value;
        },
        get: () => rotation
    });

    Object.defineProperty(this, "project", {
        set: () => {},
        get: () => projector
    });

    Object.defineProperty(this, "project_invert", {
        set: () => {},
        get: () => (projector !== null ? projector.invert : null)
    });

    const compute_projection = () => {
        projector = null;

        if (A === null || B === null) {
            return;
        }

        var A1 = A,
            B1 = B;
        var G = {type: "MultiPoint", coordinates: [A1.EN, B1.EN]}; // points group
        //log.debug(`Projection.compute_projection IN A1.XY = [${A1.X},${A1.Y}], B1.XY = [${B1.X},${B1.Y}]`);
        var W = [
            [minX(A1, B1), minY(A1, B1)],
            [maxX(A1, B1), maxY(A1, B1)]
        ]; // compute window

        //log.debug("Projection.compute_projection W:", {W});
        //log.debug("Projection.compute_projection G:", G);
        
        if (A.X === 0 && B.Y === 0) {

            // The background is a GIS, projection is simplified (no rotation)
            projector = build_projector(d3.geoMercator());

            // fitting projection to the window and forcing the point group into it
            projector.projection = projector.projection.fitExtent(W, G);
        } else {

            // if geonodes A and B are ont the same horizontal line, minY = maxY = 0 
            // and the projection returns the same value whatever the input
            // fix this with adding a minimum kind of "padding" of 10

            let W_height = W[1][1] - W[0][1];
            //log.debug("Projection.compute_projection W_height:", {W_height});
            if (W_height < 10) {
                // add kind of padding to the extent
                W[0][1] = W[0][1] - 5;
                W[1][1] = W[1][1] + 5;
                //log.debug("Projection.compute_projection add padding to height", W);
            }

            let W_width = W[1][0] - W[0][0];
            //log.debug("Projection.compute_projection W_width:", {W_width});
            if (W_width < 10) {
                // add kind of padding to the extent
                W[0][0] = W[0][0] - 5;
                W[1][0] = W[1][0] + 5;
                //log.debug("Projection.compute_projection add padding to width", W);
            }

            var C = new Point();
            C.EN = d3.geoCentroid(G); // compute centroid position in EN

            // building a first projector
            projector = build_projector(d3.geoMercator().rotate([-C.E, -C.N, 0]));

            // we can't do anything else if A == B
            if (dE(A1, B1) === 0 && dN(A1, B1) === 0) {
                return;
            }

            // fitting projection to the window and forcing the point group into it
            projector.projection = projector.projection.fitExtent(W, G);

            // creating A2 and B2 which are projected images of A1 and B1
            var A2 = new Point(),
                B2 = new Point();
            A2.XY = projector(A1.EN);
            B2.XY = projector(B1.EN);
            //log.debug(`Projection.compute_projection A2.XY = [${A2.X},${A2.Y}], B2.XY = [${B2.X},${B2.Y}]`);

            // projecting the centroid
            C.XY = projector(C.EN);

            // computing the needed rotation depending on the bearing between A1B1 and A2B2
            //log.debug("Projection.compute_projection ", {A2, A1, C});
            var TA = points_bearing(A2, A1, C);
            //log.debug("Projection.compute_projection TA:", {TA});

            //log.debug("Projection.compute_projection:", {B2, B1, C});
            var TB = points_bearing(B2, B1, C);
            //log.debug("Projection.compute_projection TB:", {TB});

            var T = aT(TA, TB);
            //log.debug("Projection.compute_projection T:", {T});

            // rotating the projection with T around the centroid and fit to the window
            projector.projection = d3
                .geoMercator()
                .rotate([-C.E, -C.N, T * DEGREES])
                .fitExtent(W, G);

            //log.debug(`Projection.compute_projection A1.XY = [${A1.X},${A1.Y}], B1.XY = [${B1.X},${B1.Y}]`);

            // computing mirror points coordinates
            A2.EN = projector.invert(A1.XY);
            B2.EN = projector.invert(B1.XY);
            //log.debug(`Projection.compute_projection A2.EN = [${A2.E},${A2.N}], B2.EN = [${B2.E},${B2.N}]`);

            // maybe we still have deformation (compression) because the operator
            // may have placed coordinates wrongly, this needs to be compensated

            // as we have a rank defficient problem, we are doing the necessary computation for the SVD here
            // see https://en.wikipedia.org/wiki/Point_set_registration for reference

            if (dE(A1, B1) !== 0) {
                projector.skewE(A1, B1, A2, B2);
            }

            if (dN(A1, B1) !== 0) {
                projector.skewN(A1, B1, A2, B2);
            }
        }

        // projection is fully operational
        // don't ask for position for agnet user
        /*if (global.configuration.user.agnet === undefined) {
            window.setTimeout(() => {
                global.theUser.refresh_position();
            }, 0);
        }*/
    };

    this.export = () => {
        var data = {};
        if (A !== null) {
            let {X, Y, E, N} = A;
            data.A = {X, Y, E, N};
        } else {
            data.A = null;
        }
        if (B !== null) {
            let {X, Y, E, N} = B;
            data.B = {X, Y, E, N};
        } else {
            data.B = null;
        }

        data.rotation = rotation;

        return data;
    };

    this.import = (data) => {
        log.debug("Projection.import");
        this.rotation = data.rotation;
        if (data.A !== undefined) {
            this.add_point("A", data.A);
        } else {
            this.remove_point("A");
        }
        if (data.B !== undefined) {
            this.add_point("B", data.B);
        } else {
            this.remove_point("B");
        }

    };

    this.reset = () => {
        log.debug("Projection.reset");
        A = null;
        B = null;
        this.rotation = 0;
        projector = null;
    };

    /**
     * Save projection data in Objects media layer
     */
    this.save = async () => {
        log.debug("Projection.save");
        await context.set_projection_data(this.export());
    };

    let object_on_creating = ({object_data}) => {
        log.debug("Projection.object_on_creating", {object_data});
        if (object_data.layer_name !== "media") {
            return;
        }
        if (object_data?.attributes?.dataProjection === undefined) {
            return;
        }
        if (object_data.attributes.dataProjection === null) {
            return;
        }
        this.import(object_data.attributes.dataProjection);
    };

    let object_on_updating = ({object_data}, modifications) => {
        log.debug("Projection.object_on_updating", {object_data, modifications});
        if (object_data.layer_name !== "media") {
            return;
        }
        let {object_data: object_data_modifications} = json_inflate(modifications);
        let data = object_data_modifications?.attributes?.dataProjection;
        if (data === undefined) {
            return;
        }
        if (data === null) {
            this.reset();
            return;
        }
        if (data.A !== undefined) {
            this.add_point("A", data.A);
        } else if (data.A === null) {
            this.remove_point("A");
        }
        if (data.B !== undefined) {
            this.add_point("B", data.B);
        } else if (data.B === null) {
            this.remove_point("B");
        }

        if (data.rotation !== undefined) {
            this.rotation = data.rotation;
        }
    };

    let object_on_deleting = ({object_data}) => {
        if (object_data.layer_name !== "media") {
            return;
        }
        this.reset();
    };

    this.init = async () => {
        log.debug("Projection.init");
        context.on("objects", "creating", object_on_creating);
        context.on("objects", "updating", object_on_updating);
        context.on("objects", "deleting", object_on_deleting);
        let data = await context.projection_data;
        if (data === null) {
            return;
        }
        log.debug("Projection.init", data);
        this.import(data);
    };

    this.deinit = async () => {
        log.debug("Projection.deinit");
        context.off("objects", "creating", object_on_creating);
        context.off("objects", "updating", object_on_updating);
        context.off("objects", "deleting", object_on_deleting);
    };
};

export default Projection;
